import './Opportunity.scss'
import React, { useState, useEffect } from 'react'
import { useNavigate, NavLink } from 'react-router-dom'
import Modal from '../../components/Modal'
import { useModal } from '../../hooks/useModal'
import { type FormikErrors, useFormik } from 'formik'
import { toast } from 'react-toastify'
import ReactPaginate from 'react-paginate'
import Input from '../../components/Input/Input'
import axiosIct from '../../utils/axios'
import { RoutesEnum } from '../../core/enums'
import Loader from '../../components/Loader/Loader'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import RatingsSlider from '../../components/RatingSlider/RatingSlider'
import { type Company } from '../../core/types'
import { ReactComponent as NoOpportunity } from '../../icons/noopportunity.svg'
import { ReactComponent as SettingsLogo } from '../../icons/settings.svg'
import { Add } from '@mui/icons-material'


type Ratings = Record<string, number>
type Weights = Record<string, number>

export interface OppInput {

  oppname: string
  oppdescription: string
  oppbudget: number
  oppratings: Ratings
}

export interface Opportunity {
  id: string
  name: string
  description: string
  budget: number
  ratings: Ratings
  created_date: string
  updated_date: string
  score: number
}

const criteria = [
  { 
    key: 'strategicAlignment', 
    label: 'Strategic Alignment', 
    min: 1, 
    max: 5,
    explanation: 'Assess the degree to which the initiative aligns with your strategic objectives and long-term goals. <br/>Initiatives that closely align with the strategic direction should receive higher priority.<br />High Strategic Alignment = 5<br />Low Strategic Alignment = 1' 
  },
  { 
    key: 'increaseRevenueOrCostReduction', 
    label: 'Increase Revenue or Cost Reduction', 
    min: 1, 
    max: 5,
    explanation: 'Consider the ability of the initiative to provide additional revenue or reduce costs.<br /> Initiatives that can deliver increased/new revenue or provide methods to reduce costs should receive higher priority.<br />High Revenue Increase or Cost Reduction = 5 <br /> Low Revenue Increase or Cost Reduction = 1' 
  },
  { 
    key: 'operationalEffectiveness', 
    label: 'Operational Effectiveness', 
    min: 1, 
    max: 5,
    explanation: 'Evaluate the potential impact of the initiative to determine if there will be an increase in operational capacity or <br />alleviation of pain associated with poorly engineered processes, proceedures, or policies.<br /> Initiatives that provide higher effectiveness or significantly reduce pain should receive higher priority.<br />High Effectiveness or Alleviation of Pain = 5<br />Low Effectiveness or Alleviation of Pain = 1'
  },
  { 
    key: 'stakeholderImpact', 
    label: 'Stakeholder Impact', 
    min: 1, 
    max: 5 ,
    explanation: 'Assess the impact of the initiative on various stakeholders, including internal teams, customers, partners, or the community.<br /> Initiatives that have a significant positive impact on stakeholders should receive higher priority.<br />High Positive Impact = 5<br />Low Positive Impact = 1'
  },
  { key: 'marketDemand', 
    label: 'Market Demand', 
    min: 1, 
    max: 5,
    explanation: 'Assess the initiative\'s alignment with current market trends, customer demands, or emerging opportunities.<br /> Initiatives that address a significant market need or capitalize on a new trend should receive higher priority.<br />High Market Demand = 5<br />Low Market Demand = 1'
   },
  { 
    key: 'innovationOrCompetitiveAdvantage', 
    label: 'Innovation or Competitive Advantage', 
    min: 1, 
    max: 5,
    explanation: 'Evaluate the potential of the initiative to introduce innovative ideas, technologies, or approaches that can provide a competitive advantage.<br /> Initiatives that offer unique and groundbreaking solutions should receive higher priority.<br />High Innovation/Advantage = 5<br /> Low Innovation/Advantage = 1'
  },
  { 
    key: 'costBenefit', 
    label: 'Cost Benefit', 
    min: 1, 
    max: 5,
    explanation: 'Considering both the investment required and the potential returns.<br /> Initiatives with a higher return on investment or cost-saving potential should receive higher priority.<br />Low Cost to Benefit = 5<br />High Cost to Benefit = 1'
   }
]

const weightsCriteria = [
  { 
    key: 'strategicAlignment', 
    label: 'Strategic Alignment', 
    min: 10, 
    max: 50,
    explanation: 'Assess the degree to which the initiative aligns with your strategic objectives and long-term goals. <br/>Initiatives that closely align with the strategic direction should receive higher priority.<br />High Strategic Alignment = 5<br />Low Strategic Alignment = 1' 
  },
  { 
    key: 'increaseRevenueOrCostReduction', 
    label: 'Increase Revenue or Cost Reduction', 
    min: 10, 
    max: 50,
    explanation: 'Consider the ability of the initiative to provide additional revenue or reduce costs.<br /> Initiatives that can deliver increased/new revenue or provide methods to reduce costs should receive higher priority.<br />High Revenue Increase or Cost Reduction = 5 <br /> Low Revenue Increase or Cost Reduction = 1' 

  },
  { 
    key: 'operationalEffectiveness', 
    label: 'Operational Effectiveness', 
    min: 10, 
    max: 50,
    explanation: 'Evaluate the potential impact of the initiative to determine if there will be an increase in operational capacity or <br />alleviation of pain associated with poorly engineered processes, proceedures, or policies.<br /> Initiatives that provide higher effectiveness or significantly reduce pain should receive higher priority.<br />High Effectiveness or Alleviation of Pain = 5<br />Low Effectiveness or Alleviation of Pain = 1' 
  },
  { 
    key: 'stakeholderImpact', 
    label: 'Stakeholder Impact', 
    min: 10, 
    max: 50,
    explanation: 'Assess the impact of the initiative on various stakeholders, including internal teams, customers, partners, or the community.<br /> Initiatives that have a significant positive impact on stakeholders should receive higher priority.<br />High Positive Impact = 5<br />Low Positive Impact = 1'
 
  },
  { 
    key: 'marketDemand', 
    label: 'Market Demand', 
    min: 10, 
    max: 50,
    explanation: 'Assess the initiative\'s alignment with current market trends, customer demands, or emerging opportunities.<br /> Initiatives that address a significant market need or capitalize on a new trend should receive higher priority.<br />High Market Demand = 5<br />Low Market Demand = 1' 
  },
  { 
    key: 'innovationOrCompetitiveAdvantage', 
    label: 'Innovation or Competitive Advantage', 
    min: 10, 
    max: 50,
    explanation: 'Evaluate the potential of the initiative to introduce innovative ideas, technologies, or approaches that can provide a competitive advantage.<br /> Initiatives that offer unique and groundbreaking solutions should receive higher priority.<br />High Innovation/Advantage = 5<br /> Low Innovation/Advantage = 1'
  },
  { 
    key: 'costBenefit', 
    label: 'Cost Benefit', 
    min: 10, 
    max: 50,
    explanation: 'Considering both the investment required and the potential returns.<br /> Initiatives with a higher return on investment or cost-saving potential should receive higher priority.<br />Low Cost to Benefit = 5<br />High Cost to Benefit = 1' 
  }
]

export default function OppDashboard (): React.ReactElement {
  const navigate = useNavigate()
  const [currentItems, setCurrentItems] = useState<Opportunity[]>([])
  const [loading, setLoading] = useState(true)
  const clientId = new URLSearchParams(location.search).get('Client') ?? ''
  const [pageNo, setPageNo] = useState<number>(1)
  const limitValue = '10'
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null)
  const [weights, setWeights] = useState<Weights>({})
  const [company, setCompany] = useState<Company>()

  const {
    isModalOpened: isOppModalOpened,
    closeModal: closeOppModal,
    openModal: openOppModal
  } = useModal()

  const {
    isModalOpened: isSettingsModalOpened,
    closeModal: closeSettingsModal,
    openModal: openSettingsModal
  } = useModal()

  const formik = useFormik<OppInput>({
    initialValues: {
      oppname: '',
      oppdescription: '',
      oppbudget: 0,
      oppratings: criteria.reduce<Record<string, number>>((acc, curr) => {
        acc[curr.key] = curr.min
        return acc
      }, {})
    },
    validate: (values) => {
      const errors: FormikErrors<OppInput> = {}
      if (values.oppname.trim() === '') {
        errors.oppname = 'Required'
      }
      if (values.oppbudget === 0 || isNaN(values.oppbudget)) {
        errors.oppbudget = 'Budget must be a number'
      }
      if (values.oppbudget < 0) {
        errors.oppbudget = 'Budget must be a positive number'
      }
      return errors
    },
    onSubmit: async (values) => {
      if (values.oppdescription.trim() === '' || values.oppdescription === '<p><br></p>') {
        toast.error('Please provide a description', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark'
        })
        return
      }
      setLoading(true)
      try {
        if (editingOpportunity !== null) {
          // Update existing opportunity
          await axiosIct.put(`/opportunities/${editingOpportunity?.id}`, {
            name: values.oppname,
            description: values.oppdescription,
            budget: values.oppbudget,
            ratings: values.oppratings,
            client_id: clientId
          })
          toast.success('Initiative Updated Successfully', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: 'dark'
          })
        } else {
          // Create a new opportunity
          await axiosIct.post('/opportunities/', {
            name: values.oppname,
            description: values.oppdescription,
            budget: values.oppbudget,
            ratings: values.oppratings,
            client_id: clientId
          })
          toast.success('Initiative Saved Successfully', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: 'dark'
          })
        }
        closeOppModal()
        await fetchWeights()
        await fetchOpportunities()
      } catch (error) {
        console.log(error)
        navigate(`${RoutesEnum.Opportunities}?Client=${encodeURIComponent(clientId ?? '')}`)
        toast.error('Error In Saving the Initiative. Please try again later.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark'
        })
      } finally {
        setLoading(false)
      }
    }
  })

  const fetchOpportunities = async (): Promise<void> => {
    setLoading(true)
    try {
      const response = await axiosIct.get(`/opportunities/?Client=${clientId}`)
      setCurrentItems(response.data)
    } catch (error) {
      console.error('Error fetching opportunities:', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteOpportunity = async (): Promise<void> => {
    setLoading(true)
    try {
      if (editingOpportunity !== null) {
        // Update existing opportunity
        await axiosIct.delete(`/opportunities/${editingOpportunity?.id}`)
        toast.success('Initiative Deleted Successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark'
        })
      } else {
        toast.error('Error In Deleting the Initiative. Please try again later.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark'
        })
      }
      await fetchWeights()
      await fetchOpportunities()
      navigate(`${RoutesEnum.Opportunities}?Client=${encodeURIComponent(clientId ?? '')}`)
    } catch (error) {
      console.log(error)
      navigate(`${RoutesEnum.Opportunities}?Client=${encodeURIComponent(clientId ?? '')}`)
      toast.error('Error In Deleting the Initiative. Please try again later.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark'
      })
    } finally {
      setLoading(false)
    }
  }

  // Fetch weights from the server
  const fetchWeights = async (): Promise<void> => {
    setLoading(true)
    try {
      const response = await axiosIct.get(`/clients/${clientId}`)
      setWeights(response.data.opportunity_weights)
      setCompany(response.data)
    } catch (error) {
      console.error('Error fetching weights:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch opportunities on component mount
  useEffect(() => {
    const doAsync = async (): Promise<void> => {
      setLoading(true)
      await fetchOpportunities()
      await fetchWeights()
    }
    void doAsync()
  }, [])

  const openOppDetail = (opportunity: Opportunity): void => {
    setEditingOpportunity(opportunity)
    // Set Formik values with explicit checks
    formik.setValues({
      oppname: opportunity.name,
      oppdescription: opportunity.description,
      oppbudget: opportunity.budget ?? 0, // Explicit check for budget
      oppratings: opportunity.ratings
    }).catch(console.error)
    openOppModal()
  }

  // Handle pagination change
  const handlePageOnChange = (selectedPage: number): void => {
    setPageNo(selectedPage)
  }

  // Save weights to the server
  const saveWeights = async (): Promise<void> => {
    setLoading(true)
    if (typeof company === 'object' && company !== null && typeof company.company_id === 'string' && company.company_id.trim() !== '') {
      const updatedCompany = {
        ...company,
        opportunity_weights: weights
      }
      setCompany(updatedCompany)
      try {
        await axiosIct.put(`/clients/${clientId}`, updatedCompany)
        toast.success('Weights saved successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark'
        })
        closeSettingsModal()
      } catch (error) {
        console.error('Error saving weights:', error)
        toast.error('Failed to save weights', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark'
        })
      } finally {
        await fetchOpportunities()
        setLoading(false)
      }
    }
  }

  // Handle weight change
  const handleWeightChange = (key: string, value: number): void => {
    setWeights(prev => ({
      ...prev,
      [key]: value
    }))
  }
  const getPriorityText = (index: number): string => {
    const priorities = ['1st', '2nd', '3rd', '4th', '5th']
    if (index < 5){
      return `${priorities[index]} Priority`
    }
    else {
      return `${index + 1}th Priority`
    }
  }

  return (
    <>
      {loading && <Loader />}
      <div className="opportunities d-flex flex-column">
        <div className="d-lg-flex">
          <div className="opportunities__content">
          <div className="opportunities-header">

            <div className="opportunities__header__title">Opportunities</div>

            {/* Right-aligned Actions in Two Rows */}
            <div className="opportunities__header__actions">
              <div className="actions-row">
                <button
                  className="btn--add-opp"
                  onClick={() => { formik.resetForm(); setEditingOpportunity(null); openOppModal() }}
                >
                  Add New
                  <Add className='icon--add-opp' style={{ color: '#ffffff' }} />
                </button>

                <SettingsLogo className='opportunities__settings' onClick={() => { openSettingsModal() }} />
              </div>

              <div className="sort-row">
                <select
                  className="opportunities__sort-dropdown"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = e.target.value
                    setCurrentItems((prevItems) => {
                      const sortedItems = [...prevItems]
                      if (value === 'highest') {
                        sortedItems.sort((a, b) => b.score - a.score)
                      } else if (value === 'lowest') {
                        sortedItems.sort((a, b) => a.score - b.score)
                      }
                      return sortedItems
                    })
                  }}
                >
                  <option value="default">Sort by</option>
                  <option value="highest">Highest Score</option>
                  <option value="lowest">Lowest Score</option>
                </select>
              </div>
          </div>
          </div>
            {!loading && currentItems.map((item, index) => (
              <div className="post-box active" key={item.id} onClick={() => { openOppDetail(item) }}>
                  {/* Header Row: Title, Score, Budget */}
                  <div className="post-box-header">
                    <div className="priority">
                      {getPriorityText(index)}
                    </div>
                    <h6 className="budget">${item.budget.toLocaleString()}</h6>
                  </div>

                  {/* Details */}
                  <div className="post-box-details">
                    <div className="score">
                      Score: {item.score}
                    </div>
                    <div className="budget-label">
                      Budget
                    </div>
                  </div>

                  {/* Title and Description */}
                  <div className="post-box__title">{item.name}</div>
                  <div className="post-box__description">
                    <div dangerouslySetInnerHTML={{ __html: item.description }} />
                  </div>
                </div>
            ))}
            {!loading && currentItems.length === 0 && (
              <div className='opportunities_no_result'>
                <NoOpportunity />
              </div>
            )}
            {!loading && currentItems.length > 0 && (
              <ReactPaginate
                previousLabel="<"
                nextLabel=">"
                breakLabel="..."
                className="react-pagination p-0"
                pageCount={Math.ceil(currentItems.length / parseInt(limitValue))}
                pageRangeDisplayed={5}
                onPageChange={(e) => { handlePageOnChange(e.selected + 1) }}
                forcePage={pageNo - 1}
                nextClassName='next'
                previousClassName='next'
                disabledClassName='disable'
              />
            )}
          </div>
        </div>
      </div>
      {/* Opportunity Modal */}
      <Modal isOpened={!!isOppModalOpened} closeModal={closeOppModal} title={editingOpportunity !== null ? 'Edit Opportunity' : 'New Opportunity'}>
          {/* <h2 className="modal__applied__title">New Opportunity</h2> */}
          <div className="modal__applied__text">
            Please let us know about any the new initiative, that you would like to be added.
          </div>
          <form className='form w-100' onSubmit={formik.handleSubmit}>
            <Input
              name='oppname'
              label='Initiative Name'
              value={formik.values.oppname}
              onChange={formik.handleChange}
              error={(formik.touched.oppname ?? false) && formik.errors.oppname}
            />
            <div className="Input__label fw-bold mb-2" style={{ color: '#415473' }}>Initiative Description</div>
            <RichTextEditor
              text={formik.values.oppdescription}
              onChange={(content: string) => {
                formik.setFieldValue('oppdescription', content, true).catch(console.error)
              }}
            />
              <Input
                name='oppbudget'
                type='text'
                label='LOE/Budget'
                value={formik.values.oppbudget.toString()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  if (/^\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue('oppbudget', value === '' ? 0 : parseFloat(value))
                      .catch(console.error) // Handle the promise with catch
                  }
                }}
                onBlur={() => {
                  // Use void to explicitly ignore the returned Promise
                  void formik.validateField('oppbudget').catch(console.error)
                }}
                error={(formik.touched.oppbudget ?? false) && formik.errors.oppbudget}
              />
            <div className="Input__label fw-bold mb-2" style={{ color: '#415473' }} >Category Ratings</div>
            <RatingsSlider
              criteria={criteria}
              ratings={formik.values.oppratings}
              onChange={(key: string, value: number) => {
                formik.setFieldValue(`oppratings.${key}`, value, true).catch(console.error)
              }}
              step={0.5}
            />
            <div className="modal-footer">
              <button className="save-btn">
                {editingOpportunity !== null ? 'Update' : 'Add Opportunity'}
              </button>

              {editingOpportunity !== null && (
                <span className="delete-opportunity" onClick={() => { deleteOpportunity().catch(console.error) }}>
                  Delete Opportunity
                </span>
              )}
            </div>
          </form>
        </Modal>

      {/* Settings Modal */}
      <Modal isOpened={isSettingsModalOpened} closeModal={closeSettingsModal} title="Adjust Weights">
        {loading && <Loader />}
        {!loading && (
          <div className="weights-slider-container">
            {weightsCriteria.map((criterion: any) => (
              <div key={criterion.key} className="weight-slider">
                <RatingsSlider
                  criteria={[criterion]}
                  ratings={{ [criterion.key]: weights[criterion.key] ?? criterion.min }}
                  onChange={handleWeightChange}
                  step={5}
                />
              </div>
            ))}
            <button className="save-btn" type='submit' onClick={() => {
              (async () => {
                await saveWeights()
              })().catch(console.error)
            }}>
              Save Weights
            </button>
          </div>
        )}
      </Modal>
    </>
  )
}
